import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { ButtonPrimary } from 'components/Button'
import CurrencyLogo from 'components/CurrencyLogo'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { useApiTokenValue } from 'hooks/useTokenPrice'
import React from 'react'
import styled from 'styled-components'
import { ThemedText } from 'theme'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import TransactionError from '../TransactionError'

// Styled components
const ClaimModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
`

const RewardSummary = styled.div`
  background: ${({ theme }) => theme.cardSecondary};
  border-radius: 12px;
  padding: 16px;
`

const RewardHeading = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
`

const RewardItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundInteractive};

  &:last-child {
    border-bottom: none;
  }
`

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TotalValue = styled.div`
  display: flex;
  justify-content: flex-end;
  font-weight: 600;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.backgroundInteractive};
  margin-top: 8px;
`

const ActionInfo = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  line-height: 1.5;
`

type KodiakFarmClaimStepProps = {
  next: () => void
  onStart: () => Promise<string | null>
  earnedAmount: Array<{
    token: Token | null | undefined
    amount: CurrencyAmount<Token> | undefined
  }>
  error?: string
} & Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'>

export default function KodiakFarmClaimStep({ next, onStart, earnedAmount, error, ...rest }: KodiakFarmClaimStepProps) {
  const { attemptingTxn } = rest

  // Calculate total earned value in USD
  const totalEarnedUSD = earnedAmount.reduce((total, item) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { value: fiatValue } = useApiTokenValue(item.amount) || 0
    return total + (fiatValue ?? 0)
  }, 0)

  return (
    <ConfirmationTransactionStep {...rest} onConfirm={next}>
      <ClaimModalContainer>
        <RewardSummary>
          <RewardHeading>Available rewards to claim:</RewardHeading>

          {earnedAmount.map((item, index) => (
            <RewardItem key={index}>
              <TokenInfo>
                <CurrencyLogo currency={item.token} size="20px" />
                <ThemedText.DeprecatedBody fontWeight="500">{item.token?.symbol}</ThemedText.DeprecatedBody>
              </TokenInfo>
              <ThemedText.DeprecatedBody>{item.amount?.toSignificant(6) || '0'}</ThemedText.DeprecatedBody>
            </RewardItem>
          ))}

          <TotalValue>
            <ThemedText.DeprecatedBody fontWeight="600">{formatDollarAmount(totalEarnedUSD)}</ThemedText.DeprecatedBody>
          </TotalValue>
        </RewardSummary>

        <ActionInfo>Claiming your farm rewards will transfer them to your connected wallet.</ActionInfo>

        <ButtonPrimary onClick={onStart} disabled={attemptingTxn || earnedAmount.length === 0}>
          {attemptingTxn ? 'Claiming...' : 'Confirm Claim'}
        </ButtonPrimary>
        {error && <TransactionError error={error} />}
      </ClaimModalContainer>
    </ConfirmationTransactionStep>
  )
}
